import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, WORKER_API_BASE } from '../lib/api';
import { useToast } from '../components/Toast';
import EncryptedPdfModal from '../components/EncryptedPdfModal';
import AutoGenerateSuggestionPanel from '../components/AutoGenerateSuggestionPanel';
import { Upload, FileText, Image, File, Loader2, AlertCircle, CheckCircle2, AlertTriangle, ArrowRight, Landmark, CreditCard, Receipt, Hourglass } from 'lucide-react';
import { tr } from '../lib/i18nHelpers';
import { writeTokenUsage, clearTokenUsage } from '../components/TokenPopup';
import { validateFiles, ACCEPT_ATTR, MAX_FILE_BYTES, type RejectedFile, type RejectReason } from '../lib/fileValidation';
import { renderPdfPagesToImages } from '../lib/pdfRaster';
import { shouldRunVlFallback } from '../lib/vlFallback';
import { stageImportMarker } from '../lib/importNotices';
import { failedDraftRoute, applyOcrFallback } from '../lib/ocrFailure';
import { postUploadPlan, stashBatchHighlightIds, takeBatchHighlightIds, bankDuplicateNoticePath } from '../lib/ocrHighlight';
import { useActiveOcrJobs, type ActiveJob } from '../lib/useActiveOcrJobs';
import { useDateFilter } from '../contexts/DateFilterContext';

// ── Channel types ─────────────────────────────────────────────────────────

type UploadChannel = 'bank_statement' | 'card_statement' | 'sales_invoice' | 'purchase_invoice' | 'petty_cash' | 'others' | 'bank_document_collection';

type FileUploadStatus = 'pending' | 'processing' | 'queued' | 'review' | 'imported' | 'error';

interface ChannelDef {
  key: UploadChannel;
  label: string;
  labelZh: string;
  labelCn: string;
  folder: string;
  category: string;
  direction?: 'outgoing' | 'incoming';
}

const CHANNELS: ChannelDef[] = [
  { key: 'bank_statement', label: 'Bank Statement', labelZh: '銀行月結單', labelCn: '银行月结单', folder: 'Bank Statements', category: 'bank_statement' },
  {
    key: 'bank_document_collection',
    label: 'Bank Statements + Supporting Bank Documents',
    labelZh: '銀行月結單及支援銀行文件',
    labelCn: '银行月结单及支持银行文件',
    folder: 'Bundles',
    category: 'bank_document_collection',
  },
  { key: 'card_statement', label: 'Card Statement', labelZh: '信用卡月結單', labelCn: '信用卡月结单', folder: 'Card Statements', category: 'card_statement' },
  { key: 'sales_invoice', label: 'Sales Invoice', labelZh: '銷售發票', labelCn: '销售发票', folder: 'Invoices', category: 'invoice', direction: 'outgoing' },
  { key: 'purchase_invoice', label: 'Purchase Invoice', labelZh: '採購發票', labelCn: '采购发票', folder: 'Invoices', category: 'invoice', direction: 'incoming' },
  { key: 'petty_cash', label: 'Petty Cash', labelZh: '零用金', labelCn: '零用金', folder: 'Petty Cash', category: 'petty_cash' },
  { key: 'others', label: 'Others (Receipts, Cash Payments etc.)', labelZh: '其他（收據、現金付款等）', labelCn: '其他（收据、现金付款等）', folder: 'Others', category: 'general' },
];

function channelLabel(ch: ChannelDef): string {
  return tr(ch.label, ch.labelZh, ch.labelCn);
}

function rejectionMessage(r: RejectReason): string {
  const maxMb = MAX_FILE_BYTES / 1024 / 1024;
  switch (r.kind) {
    case 'apple_image': {
      const fmt = r.ext.slice(1).toUpperCase();
      return tr(
        `${fmt} photos can't be read. On iPhone: Settings › Camera › Formats › "Most Compatible", or export the photo as JPEG first.`,
        `無法讀取 ${fmt} 相片。請於 iPhone 設定 › 相機 › 格式 › 選擇「最相容」，或先將相片匯出為 JPEG。`,
        `无法读取 ${fmt} 照片。请于 iPhone 设置 › 相机 › 格式 › 选择「最兼容」，或先将照片导出为 JPEG。`,
      );
    }
    case 'unsupported':
      return tr(
        `${r.ext || 'This file type'} is not supported. Use PDF, PNG, JPG, WEBP or GIF.`,
        `不支援 ${r.ext || '此文件類型'}。請使用 PDF、PNG、JPG、WEBP 或 GIF。`,
        `不支援 ${r.ext || '此文件类型'}。请使用 PDF、PNG、JPG、WEBP 或 GIF。`,
      );
    case 'too_large': {
      const mb = (r.bytes / 1024 / 1024).toFixed(1);
      return tr(
        `Too large (${mb} MB). Maximum is ${maxMb} MB.`,
        `文件過大（${mb} MB）。上限為 ${maxMb} MB。`,
        `文件过大（${mb} MB）。上限为 ${maxMb} MB。`,
      );
    }
  }
}

function reviewPageFlags(result: any): string {
  const params = new URLSearchParams();
  if (result?.needs_direction_review) params.set('review_direction', '1');
  if (result?.company_not_detected) params.set('company_not_detected', '1');
  if (result?.is_duplicate) params.set('is_duplicate', '1');
  if (result?.duplicate_blocked) params.set('dup_blocked', '1');
  if (result?.duplicate_status) params.set('dup_status', result.duplicate_status);
  if (result?.new_counterparty) params.set('new_company', '1');
  if (result?.direction) params.set('direction', result.direction);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ── Mismatch dialog ────────────────────────────────────────────────────────

interface MismatchInfo {
  channel: ChannelDef;
  detectedType: string;
  inferredValues: Record<string, string>;
  fileId: string;
  result: any;
  fileName: string;
}

function MismatchDialog({ info, onForce, onSwitch, onClose }: {
  info: MismatchInfo;
  onForce: () => void;
  onSwitch: () => void;
  onClose: () => void;
}) {
  const detectedChannel = CHANNELS.find(c => c.key === info.detectedType);
  const detectedLabel = detectedChannel ? channelLabel(detectedChannel) : info.detectedType;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card border rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 space-y-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-amber-900 dark:text-amber-100 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-bold">
              {tr('Document Type Mismatch', '文件類型不符', '文件类型不符')}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {tr(
                `This file was uploaded as "${channelLabel(info.channel)}" but OCR detected it as "${detectedLabel}".`,
                `此文件上傳為「${channelLabel(info.channel)}」，但 OCR 檢測為「${detectedLabel}」。`,
                `此文件上传为「${channelLabel(info.channel)}」，但 OCR 检测为「${detectedLabel}」。`,
              )}
            </p>
          </div>
        </div>

        {/* Inferred values table */}
        {Object.keys(info.inferredValues).length > 0 && (
          <div className="bg-muted/30 rounded-lg p-3 space-y-1">
            <p className="text-xs font-semibold text-muted-foreground mb-2">
              {tr('OCR Inferred Values:', 'OCR 推斷值：', 'OCR 推断值：')}
            </p>
            <table className="w-full text-xs">
              <tbody>
                {Object.entries(info.inferredValues).map(([key, value]) => (
                  <tr key={key} className="border-b border-border/30 last:border-b-0">
                    <td className="py-1 pr-3 font-medium text-muted-foreground capitalize">{key}</td>
                    <td className="py-1">{value || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="px-3 py-2 border rounded-lg text-sm hover:bg-muted">
            {tr('Cancel', '取消', '取消')}
          </button>
          <button onClick={onForce} className="px-4 py-2 bg-amber-900 text-white rounded-lg text-sm font-medium hover:bg-amber-800 flex items-center gap-1.5">
            {tr(`Force as ${channelLabel(info.channel)}`, `強制作為${channelLabel(info.channel)}`, `强制作为${channelLabel(info.channel)}`)}
          </button>
          <button onClick={onSwitch} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 flex items-center gap-1.5">
            {detectedChannel
              ? tr(`Switch to ${channelLabel(detectedChannel)}`, `切換至${channelLabel(detectedChannel)}`, `切换至${channelLabel(detectedChannel)}`)
              : tr('Switch channel', '切換頻道', '切换频道')}
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Upload Complete destinations ────────────────────────────────────────────
// Buttons ordered by the Bookkeeping nav precedence (bank → card → AR →
// Expenses → GJE → Collections). Mapping per user decision 2026-11-05: sales
// invoices → AR; purchase invoices / receipts / others → Expenses; petty cash
// posts a journal entry → GJE; collections → Bundles.

const DEST_ORDER = ['bank_statement', 'card_statement', 'ar', 'expenses', 'gje', 'bundles'] as const;

function completeDestinations(
  b: { bank: number; invoice: number; card: number },
  channel: UploadChannel,
): { label: string; url: string }[] {
  const labels: Record<string, { label: string; url: string }> = {
    bank_statement: { label: tr('Bank Statements', '銀行月結單', '银行月结单'), url: '/bank-statements' },
    card_statement: { label: tr('Card Statements', '信用卡月結單', '信用卡月结单'), url: '/card-statements' },
    ar: { label: tr('Sales Invoices / AR', '銷售發票 / AR', '销售发票 / AR'), url: '/ar' },
    expenses: { label: tr('Expenses', '費用', '费用'), url: '/invoices' },
    gje: { label: tr('General Journal Entries', '日記帳分錄', '日记账分录'), url: '/GJE' },
    bundles: { label: tr('Collections', '文件收集', '文件收集'), url: '/bundles' },
  };
  const picks = new Set<string>();
  if (b.bank > 0 || channel === 'bank_statement') picks.add('bank_statement');
  if (b.card > 0 || channel === 'card_statement') picks.add('card_statement');
  if (b.invoice > 0 || channel === 'sales_invoice') picks.add('ar');
  if (b.invoice > 0 || channel === 'purchase_invoice' || channel === 'others') picks.add('expenses');
  if (channel === 'petty_cash') picks.add('gje');
  if (channel === 'bank_document_collection') picks.add('bundles');
  return DEST_ORDER.filter(k => picks.has(k)).map(k => labels[k]);
}

// ── AI OCR scanning state — the DB-backed job list rendered inline (the same
// data the fixed OcrTray shows; this is the page-level view). ────────────────
function ScanningView({ jobs }: { jobs: ActiveJob[] }) {
  return (
    <div className="bg-card border rounded-xl p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-primary shrink-0" />
        <div>
          <h3 className="font-bold">{tr('AI OCR scanning', 'AI OCR 掃描中', 'AI OCR 扫描中')}</h3>
          <p className="text-sm text-muted-foreground">
            {tr(
              'Your files are being read. This page updates automatically — you can also leave and come back.',
              '系統正在讀取您的文件。此頁面會自動更新——您也可以離開稍後再回來。',
              '系统正在读取您的文件。此页面会自动更新——您也可以离开稍后再回来。'
            )}
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {jobs.map(job => (
          <div key={job.id} className="flex items-center justify-between border rounded-lg px-3 py-2">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{job.filename}</p>
              <p className="text-xs text-muted-foreground">
                {job.pages_total > 0
                  ? tr(`Page ${job.pages_done} of ${job.pages_total}`, `第 ${job.pages_done} / ${job.pages_total} 頁`, `第 ${job.pages_done} / ${job.pages_total} 页`)
                  : tr('Preparing…', '準備中…', '准备中…')}
                {job.statements_ready > 0 && ` · ${tr(`${job.statements_ready} statement(s) ready`, `${job.statements_ready} 份月結單已就緒`, `${job.statements_ready} 份月结单已就绪`)}`}
              </p>
            </div>
            {job.pages_total > 0 && (
              <div className="w-32 shrink-0">
                <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                  <div className="bg-primary h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(100, (job.pages_done / job.pages_total) * 100)}%` }} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Upload Complete state — next-step buttons. ──────────────────────────────
function CompleteView({ dests, pendingCount, onUploadMore }: {
  dests: { label: string; url: string }[];
  pendingCount: number;
  onUploadMore: () => void;
}) {
  return (
    <div className="bg-card border rounded-xl p-6 space-y-4 text-center">
      <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto" />
      <div>
        <h3 className="text-lg font-bold">{tr('Upload complete', '上傳完成', '上传完成')}</h3>
        <p className="text-sm text-muted-foreground mt-1">
          {pendingCount > 0
            ? tr(
              `${pendingCount} file(s) need your review before they count in the books.`,
              `${pendingCount} 個文件需要覆核後才會入賬。`,
              `${pendingCount} 个文件需要复核后才会入账。`
            )
            : tr('Your files were processed and saved.', '文件已處理並儲存。', '文件已处理并储存。')}
        </p>
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        <button onClick={onUploadMore}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 flex items-center gap-1.5">
          <Upload className="h-4 w-4" /> {tr('Upload more', '繼續上傳', '继续上传')}
        </button>
        {dests.map(d => (
          <Link key={d.url} to={d.url}
            className="px-4 py-2 border rounded-lg text-sm font-medium hover:bg-muted flex items-center gap-1.5">
            {d.label} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        ))}
        <Link to="/review-queue"
          className="px-4 py-2 border rounded-lg text-sm font-medium hover:bg-muted flex items-center gap-1.5">
          {tr('Pending Review', '待審核', '待审核')} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}

// ── Idle-state stats — uploaded/pending counts per type (follows the FY/date
// selector; GET /review-queue/upload-stats). ─────────────────────────────────
function UploadStatsPanel() {
  const { startDate, endDate } = useDateFilter();
  const { data } = useQuery<{ uploaded: Record<string, number>; pending_review: Record<string, number> }>({
    queryKey: ['upload-stats', startDate, endDate],
    queryFn: () => {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      return api(`/review-queue/upload-stats?${params.toString()}`) as any;
    },
    refetchInterval: 10000,
  });
  if (!data) return null;
  const rows = [
    { key: 'bank_statement', label: tr('Bank Statements', '銀行月結單', '银行月结单'), Icon: Landmark },
    { key: 'card_statement', label: tr('Card Statements', '信用卡月結單', '信用卡月结单'), Icon: CreditCard },
    { key: 'invoice', label: tr('Invoices', '發票', '发票'), Icon: FileText },
    { key: 'receipt', label: tr('Receipts', '收據', '收据'), Icon: Receipt },
    { key: 'other', label: tr('Others', '其他', '其他'), Icon: File },
  ];
  return (
    <div className="bg-card border rounded-xl overflow-hidden">
      <div className="px-4 py-2.5 bg-muted/50 border-b flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-semibold text-sm">
          <Hourglass className="h-4 w-4 text-muted-foreground" />
          {tr('Uploaded files & pending review', '已上載文件與待覆核', '已上传文件与待复核')}
        </div>
        <Link to="/review-queue" className="text-xs text-primary hover:underline shrink-0">
          {tr('Go to Pending Review →', '前往待審核 →', '前往待审核 →')}
        </Link>
      </div>
      <div className="divide-y">
        {rows.map(({ key, label, Icon }) => {
          const uploaded = data.uploaded?.[key] || 0;
          const pending = data.pending_review?.[key] || 0;
          return (
            <div key={key} className="flex items-center justify-between px-4 py-2">
              <div className="flex items-center gap-2 text-sm">
                <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                <span>{label}</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">
                  {tr(`${uploaded} uploaded`, `${uploaded} 個已上載`, `${uploaded} 个已上传`)}
                </span>
                {pending > 0 ? (
                  <Link to="/review-queue"
                    className="px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 hover:underline">
                    {tr(`${pending} pending review`, `${pending} 個待覆核`, `${pending} 个待复核`)}
                  </Link>
                ) : (
                  <span className="text-muted-foreground/60">{tr('none pending', '無待覆核', '无待复核')}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Component ──────────────────────────────────────────────────────────────

export default function FileUpload() {
  useTranslation(); // subscribe to language changes so tr() updates
  const nav = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [dragOver, setDragOver] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  // Upload lock (2026-10-28): while another file's OCR/import is in flight,
  // this page is read-only too — same rule as the locked "More Uploads" nav.
  const { jobs: activeOcrJobs } = useActiveOcrJobs();
  const uploadsLocked = uploading || activeOcrJobs.length > 0;

  // ── Page stage machine (2026-11-05) ──
  // idle (select files + stats) → uploading (files POSTing, existing inline
  // progress) → scanning (AI OCR jobs running — ALSO the state shown when the
  // user navigates in while jobs are in flight, per the DB-backed job list) →
  // complete (next-step buttons). Single-file needs-review imports keep their
  // direct jump to the review page from uploadFile().
  const [stage, setStage] = useState<'idle' | 'scanning' | 'complete'>('idle');
  const [completeDests, setCompleteDests] = useState<{ label: string; url: string }[]>([]);
  const [completePendingCount, setCompletePendingCount] = useState(0);
  const view: 'idle' | 'uploading' | 'scanning' | 'complete' =
    uploading ? 'uploading'
    : stage === 'scanning' ? 'scanning'
    : stage === 'complete' ? 'complete'
    : 'idle';
  useEffect(() => {
    if (activeOcrJobs.length > 0) setStage('scanning');
  }, [activeOcrJobs.length]);
  useEffect(() => {
    // Drain → complete (small debounce so a poll boundary can't flicker the
    // state while a just-queued job becomes visible to the poll).
    if (stage === 'scanning' && activeOcrJobs.length === 0) {
      const t = setTimeout(() => setStage(prev => (prev === 'scanning' ? 'complete' : prev)), 1500);
      return () => clearTimeout(t);
    }
  }, [stage, activeOcrJobs.length]);
  const resetToIdle = () => {
    setStage('idle');
    setCompleteDests([]);
    setFiles([]);
    setDescription('');
    setFileErrors({});
    setFileStatuses({});
    setRejected([]);
  };
  const [processingMsg, setProcessingMsg] = useState<string | null>(null);
  const batchRef = useRef({ total: 0, done: 0, bank: 0, invoice: 0, card: 0 });
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0, currentFile: '' });
  const [tokenCardDismissed, setTokenCardDismissed] = useState(false);
  const [encryptedFile, setEncryptedFile] = useState<{ fileId: string; fileName: string } | null>(null);
  const [fileErrors, setFileErrors] = useState<Record<number, string>>({});
  const [fileStatuses, setFileStatuses] = useState<Record<number, FileUploadStatus>>({});
  const [rejected, setRejected] = useState<RejectedFile[]>([]);
  const [showAutoGenPanel, setShowAutoGenPanel] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importedIdsRef = useRef<string[]>([]);

  // Channel selection — ?channel=<key> preselects (e.g. Collections → New collection)
  const [searchParams] = useSearchParams();
  const channelParam = searchParams.get('channel') as UploadChannel | null;
  const [channel, setChannel] = useState<UploadChannel>(
    channelParam && CHANNELS.some(c => c.key === channelParam) ? channelParam : 'bank_statement'
  );
  const channelDef = CHANNELS.find(c => c.key === channel)!;

  // Mismatch dialog state
  const [mismatch, setMismatch] = useState<MismatchInfo | null>(null);
  const mismatchResolveRef = useRef<((action: 'force' | 'switch' | 'cancel') => void) | null>(null);

  function pushToQueue(docType: string, reviewId: string, filename: string, flags: string) {
    // ── Session review queue — OBSOLETE 2026-11-04 ──
    // The post-upload one-by-one review carousel was retired in favour of the
    // checklist-centric flow (postUploadPlan lands on /file-upload/review; each
    // file is reviewed from there and back). Nothing consumes the queue anymore,
    // so the writes are disabled. Kept for revival knowledge:
    // const stored = sessionStorage.getItem('reviewQueue');
    // let queue: { docType: string; reviewId: string; filename: string; flags: string }[] = [];
    // try { if (stored) queue = JSON.parse(stored); } catch {}
    // queue.push({ docType, reviewId, filename, flags });
    // sessionStorage.setItem('reviewQueue', JSON.stringify(queue));
    // sessionStorage.setItem('reviewQueueTotal', String(batchRef.current.total));
    void docType; void reviewId; void filename; void flags;
    batchRef.current.done++;
    if (docType === 'bank_statement') batchRef.current.bank++;
    else if (docType === 'invoice') batchRef.current.invoice++;
    else if (docType === 'card_statement') batchRef.current.card++;
    setBatchProgress({ done: batchRef.current.done, total: batchRef.current.total, currentFile: filename });
  }

  // Every entry path (picker, drag-drop) funnels through here. The `accept`
  // attribute only filters the OS picker — it enforces nothing and drag-drop
  // ignores it entirely — so validation has to live at this junction.
  const applySelection = useCallback((incoming: File[]) => {
    const { accepted, rejected: bad } = validateFiles(incoming);
    setFiles(accepted);
    setRejected(bad);
    setFileErrors({});
    setFileStatuses({});
  }, []);

  // ── Full-window drag & drop ──
  // In a small/resized window the drop zone is a small island, and a drop
  // that missed it fell through to the browser default: the tab navigated to
  // the dropped file and the session died. While this page is open the whole
  // window is the target — an overlay announces it, drops anywhere route
  // through the same validation as the zone, and file drags can never
  // navigate (window dragover/drop are always prevented).
  const [windowDragActive, setWindowDragActive] = useState(false);
  const dragDepthRef = useRef(0);
  useEffect(() => {
    const hasFiles = (e: Event) => {
      const dt = (e as DragEvent).dataTransfer;
      return !!dt && Array.from(dt.types || []).includes('Files');
    };
    const onDragEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepthRef.current += 1;
      setWindowDragActive(true);
    };
    const onDragOver = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
    };
    const onDragLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setWindowDragActive(false);
    };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepthRef.current = 0;
      setWindowDragActive(false);
      // The zone handles its own drops — don't double-apply.
      if ((e.target as HTMLElement)?.closest?.('div.border-dashed')) return;
      const dropped = Array.from((e.dataTransfer?.files || []) as Iterable<File>);
      if (dropped.length > 0) {
        applySelection(dropped);
        window.scrollTo({ top: 0 });
      }
    };
    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [applySelection]);

  // Guards both entry points: the button and the surrounding clickable prompt.
  const openPicker = useCallback(() => {
    if (uploading) return;
    fileInputRef.current?.click();
  }, [uploading]);

  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); setDragOver(true); }, []);
  const handleDragLeave = useCallback(() => setDragOver(false), []);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer.files.length > 0) applySelection(Array.from(e.dataTransfer.files));
  }, [applySelection]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) applySelection(Array.from(e.target.files));
    // Reset so picking the same file again still fires a change event.
    e.target.value = '';
  }, [applySelection]);

  // Extract inferred values from OCR result for display
  function extractInferredValues(result: any): Record<string, string> {
    const vals: Record<string, string> = {};
    if (result?.amount != null) vals['Amount'] = typeof result.amount === 'number' ? result.amount.toLocaleString('en-HK', { minimumFractionDigits: 2 }) : String(result.amount);
    if (result?.statement_date || result?.invoice_date) vals['Date'] = result.statement_date || result.invoice_date || '';
    if (result?.bank_name || result?.counterparty_name || result?.supplier_name) vals['Counterparty'] = result.bank_name || result.counterparty_name || result.supplier_name || '';
    if (result?.account_number || result?.invoice_number) vals['Reference'] = result.account_number || result.invoice_number || '';
    if (result?.currency) vals['Currency'] = result.currency;
    return vals;
  }

  // Show mismatch dialog and wait for user decision
  function showMismatchDialog(info: MismatchInfo): Promise<'force' | 'switch' | 'cancel'> {
    return new Promise((resolve) => {
      mismatchResolveRef.current = resolve;
      setMismatch(info);
    });
  }

  const uploadFile = async (file: File, skipNavigation = false, fileIndex = 0, totalFiles = 0): Promise<string> => {
    const token = localStorage.getItem('token');
    const activeClient = localStorage.getItem('activeClient');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
    if (activeClient) {
      try { const c = JSON.parse(activeClient); if (c?.id) headers['X-Active-Client'] = c.id; } catch {}
    }

    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });

    // Step 1: Upload to file-storage with channel's folder
    const uploadBody = {
      filename: file.name,
      original_name: file.name,
      file_type: file.type,
      file_size: file.size,
      file_data: base64,
      folder: channelDef.folder,
      description: description,
    };

    const uploadData = await api('/file-storage/upload', {
      method: 'POST', body: uploadBody, baseUrl: WORKER_API_BASE,
    }) as any;
    const fileId = uploadData?.id;
    if (!fileId) throw new Error('Upload succeeded but no file ID');

    // Step 2: Run OCR + document type detection.
    // Multi-page PDFs are OCR'd in the BACKGROUND (queue jobs, ~2-8 min). The
    // endpoint returns 202 queued; hand off to the global OCR activity tray
    // instead of blocking this upload on the background job.
    let result: any = null;
    let importRespStatus = 500;
    const importResp = await fetch(
      `${WORKER_API_BASE}/file-storage/${fileId}/import-document?channel=${encodeURIComponent(channel)}`,
      { method: 'POST', headers },
    );
    importRespStatus = importResp.status;
    if (importResp.status === 202) {
      importedIdsRef.current.push(fileId);
      // Durable marker: the completion banner survives reloads/new tabs and
      // shows "N statements ready" even if this page never sees the finish.
      stageImportMarker({ fileId, filename: file.name, channel });
      setProcessingMsg(null);
      toast.info(tr(
        `Uploaded ${file.name} — processing in the background. Track it in the OCR activity tray.`,
        `${file.name} 已上傳 — 正在背景處理。可在 OCR 活動面板追蹤。`,
        `${file.name} 已上传 — 正在后台处理。可在 OCR 活动面板追踪。`,
      ), 6000);
      return 'queued';
    }
    result = await importResp.json().catch(() => ({}));
    if (result?.ocr_text) console.log(`[OCR-RAW|${result.ocr_source || 'unknown'}]`, result.ocr_text);
    if (result?.deepseek_raw) console.log(`[LLM|${result.ai_provider || 'unknown'}|${result.ocr_source || 'unknown'}]`, JSON.parse(result.deepseek_raw));
    if (result?.usage?.total_tokens || result?.glm_usage?.total_tokens) {
      const dsTotal = result.usage?.total_tokens || 0;
      const glmTotal = result.glm_usage?.total_tokens || 0;
      writeTokenUsage({
        prompt: (result.usage?.prompt_tokens || 0) + (result.glm_usage?.prompt_tokens || 0),
        completion: (result.usage?.completion_tokens || 0) + (result.glm_usage?.completion_tokens || 0),
        total: dsTotal + glmTotal,
      });
    }
    setProcessingMsg(null);

    // Duplicate handling
    if (importRespStatus === 409) {
      if (result?.type === 'card_statement' && result?.statement_id) {
        toast.warning(tr('Duplicate card statement. Existing record kept in File Storage.', '重複的信用卡月結單。現有記錄保留在檔案儲存庫。', '重复的信用卡月结单。现有记录保留在文件存储库。'));
        return 'duplicate';
      }
      if (result?.type === 'bank_statement' && result?.statement_id) {
        toast.warning(tr('Duplicate bank statement. Existing record kept in File Storage.', '重複的銀行月結單。現有記錄保留在檔案儲存庫。', '重复的银行月结单。现有记录保留在文件存储库。'));
        return 'duplicate';
      }
      toast.warning(result?.error || tr('Duplicate file.', '重複文件。', '重复文件。'));
      return 'duplicate';
    }

    // Content-hash duplicate of a bank statement: the API returns 201 with
    // duplicate_blocked + the EXISTING statement id (it never creates a second
    // statement). Surface it as a review item so the user lands on the existing
    // record with the duplicate notice, instead of the file being silently
    // stored and left unlinked.
    const dupPath = bankDuplicateNoticePath(result);
    if (dupPath) {
      toast.warning(tr('This file was already imported — showing the existing record.', '此文件已導入過——正在顯示現有記錄', '此文件已导入过——正在显示现有记录'));
      if (skipNavigation) {
        if (result?.statement_id) pushToQueue('bank_statement', String(result.statement_id), file.name, reviewPageFlags(result));
        return 'review';
      }
      nav(dupPath);
      return 'duplicate';
    }

    // Encrypted PDF — show password modal immediately
    if (result?.status === 'password_required' || result?.type === 'encrypted_pdf') {
      setEncryptedFile({ fileId, fileName: file.name });
      return 'encrypted';
    }

    // This file produced (or will produce) a file_records row: bank/card/invoice
    // draft, clean import, or failed-OCR draft. Duplicates and encrypted files
    // returned above and are intentionally excluded.
    importedIdsRef.current.push(fileId);

    // GLM-OCR balance exhausted — non-fatal, user can still enter manually
    if (result?.glm_ocr_unavailable) {
      toast.warning(tr(
        'OCR service is out of balance. Document saved as draft — please enter details manually.',
        'OCR 服務餘額不足。文件已存為草稿 — 請手動輸入詳細資料。',
        'OCR 服务余额不足。文件已存为草稿 — 请手动输入详细资料。'
      ), 8000);
      // Don't throw — let the flow continue to create the draft
    }

    // Only run the browser-rendered VL fallback when the primary pipeline did
    // NOT already produce a usable result. For PDFs: pages → images → /ocr-images.
    if (shouldRunVlFallback(result)) {
      const isPdf = (file.type || '').includes('pdf') || /\.pdf$/i.test(file.name);
      if (isPdf) {
        try {
          setProcessingMsg(tr('Running AI vision OCR on PDF pages…', '正在對 PDF 進行 AI 視覺 OCR…', '正在对 PDF 进行 AI 视觉 OCR…'));
          console.log('[TeCS-OCR] server Qwen OCR unavailable — starting PDF→image VL fallback', file.name);
          const pages = await renderPdfPagesToImages(file);
          console.log(`[TeCS-OCR] rendered ${pages.length} page image(s)`);
          const forcedType = channel === 'sales_invoice' || channel === 'purchase_invoice' ? 'invoice' : channel;
          const vlResp = await fetch(`${WORKER_API_BASE}/file-storage/${fileId}/ocr-images`, {
            method: 'POST', headers, body: JSON.stringify({ pages, type: forcedType }),
          });
          const vlResult = await vlResp.json().catch(() => ({}));
          console.log('[TeCS-OCR] VL fallback result', vlResult);
          if (vlResult?.success) {
            applyOcrFallback(result, vlResult);
          } else {
            toast.warning(tr('AI vision OCR could not read this PDF — using standard OCR.', 'AI 視覺 OCR 無法讀取此 PDF — 已改用標準 OCR。', 'AI 视觉 OCR 无法读取此 PDF — 已改用标准 OCR。'), 8000);
          }
        } catch (e: any) {
          console.warn('[TeCS-OCR] PDF VL fallback failed:', e?.message || e);
          toast.warning(tr('AI vision OCR failed while reading this PDF — using standard OCR.', 'AI 視覺 OCR 讀取此 PDF 時失敗 — 已改用標準 OCR。', 'AI 视觉 OCR 读取此 PDF 时失败 — 已改用标准 OCR。'), 8000);
        } finally {
          setProcessingMsg(null);
        }
      } else {
        toast.warning(tr(
          'AI (Qwen) OCR is not available at the moment — using standard OCR instead.',
          'AI (Qwen) OCR 暫時不可用 — 已改用標準 OCR。',
          'AI (Qwen) OCR 暂不可用 — 已改用标准 OCR。'
        ), 8000);
      }
    }

    // OCR could not read the file, but the server already saved an EMPTY draft
    // (ocr_failed) and returned its id. This is NOT fatal — route the user to
    // the review page so they can enter the transactions manually, instead of
    // stranding them here with an unexplained error.
    const failedDraft = failedDraftRoute(result);
    if (failedDraft) {
      toast.warning(result?.error || tr(
        'Could not read this file automatically. Please enter the transactions manually on the review page.',
        '無法自動讀取此文件。請在審核頁面手動輸入交易。',
        '无法自动读取此文件。请在审核页面手动输入交易。',
      ), 8000);
      const draftFlags = failedDraft.docType === 'invoice' ? reviewPageFlags(result) : '';
      if (skipNavigation) {
        pushToQueue(failedDraft.docType, failedDraft.reviewId, file.name, draftFlags);
        return 'review';
      }
      const reviewPath = failedDraft.docType === 'bank_statement'
        ? 'bank-statements'
        : failedDraft.docType === 'card_statement'
          ? 'card-statements'
          : 'invoices';
      nav(`/${reviewPath}/review/${failedDraft.reviewId}${draftFlags}`);
      return 'review';
    }

    // Hard errors
    if (result?.error) throw new Error(result.error);

    const detectedType = result?.type;

    // Direction-based mismatch for invoice channels: detected direction must agree with the chosen tab
    const invoiceDirectionMismatch = (channel === 'sales_invoice' || channel === 'purchase_invoice')
      && detectedType === 'invoice'
      && !!result?.direction
      && result.direction !== channelDef.direction;
    const mismatchDetectedChannelKey = invoiceDirectionMismatch
      ? (result.direction === 'incoming' ? 'purchase_invoice' : 'sales_invoice')
      : detectedType;

    if ((detectedType && detectedType !== channel) || invoiceDirectionMismatch) {
      const isInvoiceChannel = channel === 'sales_invoice' || channel === 'purchase_invoice';
      const isInvoiceDetected = detectedType === 'invoice';
      if (!(isInvoiceChannel && isInvoiceDetected && !invoiceDirectionMismatch)) {
        const action = await showMismatchDialog({
          channel: channelDef,
          detectedType: mismatchDetectedChannelKey,
          inferredValues: extractInferredValues(result),
          fileId,
          result,
          fileName: file.name,
        });
        if (action === 'switch') {
          // Accept detected type: redirect to the correct review page
          if (detectedType === 'bank_statement' && result?.statement_id) {
            if (skipNavigation) { pushToQueue('bank_statement', result.statement_id, file.name, ''); return 'review'; }
            nav(`/bank-statements/review/${result.statement_id}`);
            return 'review';
          }
          if (detectedType === 'card_statement' && result?.statement_id) {
            if (skipNavigation) { pushToQueue('card_statement', result.statement_id, file.name, ''); return 'review'; }
            nav(`/card-statements/review/${result.statement_id}`);
            return 'review';
          }
          if (detectedType === 'invoice' && result?.invoice_id) {
            const flags = reviewPageFlags(result);
            if (skipNavigation) { pushToQueue('invoice', result.invoice_id, file.name, flags); return 'review'; }
            nav(`/invoices/review/${result.invoice_id}${flags}`);
            return 'review';
          }
          throw new Error(tr('Switched channel but could not find review link.', '已切換頻道但無法找到審核連結。', '已切换频道但无法找到审核连结。'));
        }
        if (action === 'cancel') {
          // Roll back: the file and its imported record already exist at this point,
          // so delete them to make cancel a true cancel (soft-deletes file + statement, hard-deletes invoice draft)
          try {
            await api(`/file-storage/${fileId}`, { method: 'DELETE', baseUrl: WORKER_API_BASE });
          } catch (e: any) {
            // Delete requires higher tier — cancel still proceeds, but warn that the file remains
            console.warn('[Upload] cancel rollback delete failed:', e?.message || e);
            toast.warning(tr(
              'Upload cancelled, but the file could not be removed. Delete it from File Storage or ask an admin.',
              '已取消上傳，但無法移除文件。請從文件存儲中刪除或聯繫管理員。',
              '已取消上传，但无法移除文件。请从文件存储中删除或联系管理员。',
            ));
          }
          throw new Error(tr('Upload cancelled.', '已取消上傳。', '已取消上传。'));
        }
        // 'force' — re-import with user's chosen type, overriding OCR detection
        if (action === 'force') {
          setProcessingMsg(tr(`Re-importing as ${channelLabel(channelDef)}…`, `重新匯入為${channelLabel(channelDef)}…`, `重新汇入为${channelLabel(channelDef)}…`));
          const forcedType = channel === 'sales_invoice' || channel === 'purchase_invoice' ? 'invoice' : channel;
          const directionParam = channelDef.direction ? `&direction=${channelDef.direction}` : '';
          const forceResp = await fetch(
            `${WORKER_API_BASE}/file-storage/${fileId}/import-document?force=true&type=${encodeURIComponent(forcedType)}${directionParam}`,
            { method: 'POST', headers }
          );
          const forceResult = await forceResp.json().catch(() => ({}));
          if (forceResult?.error && forceResp.status !== 201) {
            throw new Error(forceResult.error || tr('Re-import failed.', '重新匯入失敗。', '重新汇入失败。'));
          }
          // Use the forced result going forward
          Object.assign(result, forceResult);
          setProcessingMsg(null);
        }
      }
    }

    const needsReview = !!(result?.needs_direction_review || result?.company_not_detected || result?.needs_review || result?.parse_failed || result?.empty_transactions);

    // Route based on user-selected CHANNEL (not OCR type)
    if (channel === 'card_statement' && result?.statement_id) {
      if (needsReview) {
        if (skipNavigation) { pushToQueue('card_statement', result.statement_id, file.name, ''); return 'review'; }
        nav(`/card-statements/review/${result.statement_id}`);
        return 'review';  // prevent handleUpload from doing a default redirect over us
      }
      return 'ok';
    } else if (channel === 'bank_statement' && result?.statement_id) {
      if (needsReview) {
        if (skipNavigation) { pushToQueue('bank_statement', result.statement_id, file.name, ''); return 'review'; }
        nav(`/bank-statements/review/${result.statement_id}`);
        return 'review';  // prevent handleUpload from doing a default redirect over us
      }
      return 'ok';
    } else if ((channel === 'sales_invoice' || channel === 'purchase_invoice') && result?.invoice_id) {
      const flags = reviewPageFlags(result);
      if (needsReview) {
        if (skipNavigation) { pushToQueue('invoice', result.invoice_id, file.name, flags); return 'review'; }
        nav(`/invoices/review/${result.invoice_id}${flags}`);
        return 'review';  // prevent handleUpload from doing a default redirect over us
      }
      return 'ok';
    } else if (channel === 'petty_cash') {
      // Auto-create journal entry: debit Petty Cash Expenses (67001), credit Cash on Hand (11101)
      const pettyAmount = result?.amount || result?.total_amount || result?.total || 0;
      if (pettyAmount > 0) {
        try {
          const today = new Date().toISOString().split('T')[0];
          const entryDate = result?.statement_date || result?.invoice_date || result?.date || today;
          const desc = result?.description || result?.supplier_name || result?.counterparty_name || file.name;
          await api('/bookkeeping/entries', {
            method: 'POST',
            body: {
              entry_date: entryDate,
              description: `Petty Cash: ${desc}`,
              reference_type: 'petty_cash',
              reference_id: fileId,
              lines: [
                { account_code: '67001', account_name: 'Petty Cash Expenses', debit: pettyAmount, credit: 0, description: desc },
                { account_code: '11101', account_name: 'Cash on Hand', debit: 0, credit: pettyAmount, description: desc },
              ],
            },
          });
          toast.success(tr(
            `Petty Cash expense created: ${pettyAmount.toLocaleString('en-HK', { minimumFractionDigits: 2 })}`,
            `零用金支出已建立：${pettyAmount.toLocaleString('en-HK', { minimumFractionDigits: 2 })}`,
            `零用金支出已建立：${pettyAmount.toLocaleString('en-HK', { minimumFractionDigits: 2 })}`,
          ));
        } catch (e: any) {
          console.error('Failed to create petty cash entry:', e);
          toast.warning(tr(
            'File saved but could not auto-create expense entry. Please create manually.',
            '文件已儲存但無法自動建立支出分錄。請手動建立。',
            '文件已储存但无法自动建立支出分录。请手动建立。',
          ));
        }
      }
      return 'ok';
    } else if (channel === 'others') {
      // Just save to file storage, no special routing
      return 'ok';
    } else if (!detectedType) {
      throw new Error(tr(
        'Could not determine document type. Please check the file and try again.',
        '無法識別文件類型。請檢查文件後重試。',
        '无法识别文件类型。请检查文件后重试。',
      ));
    }
    return 'ok';
  };

  // Collection channel bypasses the per-file import flow entirely: all files
  // go up in ONE multipart POST to /api/bundles, which queues segmentation +
  // intake + matching server-side; we then land on the bundle review page.
  const uploadCollection = async () => {
    const token = localStorage.getItem('token');
    const activeClient = localStorage.getItem('activeClient');
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    const headersWithClient: Record<string, string> = {};
    if (token) headersWithClient['Authorization'] = `Bearer ${token}`;
    if (activeClient) {
      try { const c = JSON.parse(activeClient); if (c?.id) headersWithClient['X-Active-Client'] = c.id; } catch {}
    }

    setUploading(true);
    setFileStatuses(prev => {
      const next: typeof prev = {};
      for (let i = 0; i < files.length; i++) next[i] = 'processing';
      return next;
    });
    setBatchProgress({ done: 0, total: files.length, currentFile: '' });
    setProcessingMsg(tr(
      'Uploading collection — segmenting, intake and matching start automatically…',
      '正在上傳文件批次 — 自動開始分段、收錄及配對…',
      '正在上传文件批次 — 自动开始分段、收录及配对…',
    ));

    try {
      const res = await fetch(`${WORKER_API_BASE}/bundles`, {
        method: 'POST',
        headers: headersWithClient,
        credentials: 'include',
        body: fd,
      });
      const result = await res.json().catch(() => ({} as any));
      if (!res.ok) throw new Error(result?.error || tr('Collection upload failed.', '文件批次上傳失敗。', '文件批次上传失败。'));
      if (!result?.bundle_id) throw new Error(tr('Upload succeeded but no bundle was created.', '上傳成功但未建立批次。', '上传成功但未建立批次。'));

      if (result.dedup) {
        toast.info(tr(
          'Deterministic duplicate — showing existing collection.',
          '內容與現有批次完全相同 — 顯示現有文件批次。',
          '内容与现有批次完全相同 — 显示现有文件批次。',
        ), 6000);
      } else {
        toast.success(tr(
          'Collection uploaded — segmenting has started.',
          '文件批次已上傳 — 分段已開始。',
          '文件批次已上传 — 分段已开始。',
        ));
      }

      setFiles([]);
      setDescription('');
      setRejected([]);
      queryClient.invalidateQueries({ queryKey: ['bundles'] });
      setProcessingMsg(null);
      setUploading(false);
      setTimeout(() => nav(`/bundles/review/${result.bundle_id}`), 600);
      return;
    } catch (e: any) {
      const msg = e?.message || 'Unknown error';
      toast.error(msg);
      const errStatuses: typeof fileStatuses = {};
      for (let i = 0; i < files.length; i++) errStatuses[i] = 'error';
      setFileStatuses(prev => ({ ...prev, ...errStatuses }));
      setFileErrors(prev => ({ ...prev, [0]: msg }));
    }
    setUploading(false);
    setProcessingMsg(null);
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    if (channel === 'bank_document_collection') {
      await uploadCollection();
      return;
    }
    setUploading(true);
    setFileErrors({});
    setFileStatuses({});
    const isBatch = files.length > 1;

    batchRef.current = { total: files.length, done: 0, bank: 0, invoice: 0, card: 0 };
    setBatchProgress({ done: 0, total: files.length, currentFile: '' });
    clearTokenUsage();
    setTokenCardDismissed(false);
    sessionStorage.removeItem('reviewQueue');
    sessionStorage.removeItem('reviewQueueTotal');
    // Drop any stale batch-handoff ids from a previously abandoned mixed batch —
    // a later review-queue completion must not consume them.
    takeBatchHighlightIds();
    importedIdsRef.current = [];

    let ok = 0;
    let reviewCount = 0;
    let queuedCount = 0;
    let encryptedCount = 0;
    let hasError = false;
    let idx = 0;
    for (const file of files) {
      idx++;
      const fileIdx = idx - 1;
      setFileStatuses(prev => ({ ...prev, [fileIdx]: 'processing' }));
      try {
        setBatchProgress(prev => ({ ...prev, currentFile: file.name }));
        const status = await uploadFile(file, true, idx, files.length);
        if (status === 'review') reviewCount++;
        if (status === 'queued') queuedCount++;
        if (status === 'queued') setFileStatuses(prev => ({ ...prev, [fileIdx]: 'queued' }));
        else if (status === 'review') setFileStatuses(prev => ({ ...prev, [fileIdx]: 'review' }));
        else setFileStatuses(prev => ({ ...prev, [fileIdx]: 'imported' }));
        if (status === 'encrypted') {
          encryptedCount++;
          ok++;
          setFileStatuses(prev => ({ ...prev, [fileIdx]: 'processing' }));
          // Stop processing — wait for user to handle password modal
          break;
        }
        ok++;
        setFileStatuses(prev => ({ ...prev, [fileIdx]: 'imported' }));
      } catch (e: any) {
        hasError = true;
        setFileStatuses(prev => ({ ...prev, [fileIdx]: 'error' }));
        setFileErrors(prev => ({ ...prev, [fileIdx]: e.message || 'Unknown error' }));
        if (isBatch) { batchRef.current.done++; setBatchProgress(prev => ({ ...prev, done: batchRef.current.done })); }
        break;
      }
    }

    setUploading(false);

    if (hasError) return;

    // If ALL files are encrypted, stay on this page — the password modal is open
    if (encryptedCount > 0 && encryptedCount === ok) {
      toast.info(tr(
        `${encryptedCount} encrypted file(s) need a password. Enter it in the pop-up window.`,
        `${encryptedCount} 個加密文件需要密碼。請在彈出窗口中輸入。`,
        `${encryptedCount} 个加密文件需要密码。请在弹出窗口中输入。`
      ));
      // Don't clear files — user may want to re-upload or check status
      return;
    }

    if (encryptedCount > 0 && encryptedCount < ok) {
      toast.info(tr(
        `${encryptedCount} file(s) are encrypted and need a password. ${ok - encryptedCount} file(s) processed.`,
        `${encryptedCount} 個文件已加密需要密碼。${ok - encryptedCount} 個文件已處理。`,
        `${encryptedCount} 个文件已加密需要密码。${ok - encryptedCount} 个文件已处理。`
      ));
    }

    setFiles([]);
    setDescription('');
    setRejected([]);

    const storedTokens = (() => { try { const r = sessionStorage.getItem('aiTokenUsage'); return r ? JSON.parse(r) : null; } catch { return null; } })();

    if (ok > 0) {
      queryClient.invalidateQueries({ queryKey: ['file-storage'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['bookkeeping'] });

      if (batchRef.current.bank > 0 || batchRef.current.card > 0) {
        setShowAutoGenPanel(true);
      }

      if (reviewCount > 0) {
        toast.success(tr(
          `${reviewCount} file(s) need review. ${ok - reviewCount} auto-saved.`,
          `${reviewCount} 個文件需要審核。${ok - reviewCount} 個已自動儲存。`,
          `${reviewCount} 个文件需要审核。${ok - reviewCount} 个已自动储存。`,
        ));
      } else if (queuedCount > 0) {
        toast.info(tr(
          `Uploaded ${ok} file(s) — OCR is running in the background. Track progress in the activity tray.`,
          `已上傳 ${ok} 個文件 — OCR 正在背景處理。可在活動面板追蹤進度。`,
          `已上传 ${ok} 个文件 — OCR 正在后台处理。可在活动面板追踪进度。`,
        ), 7000);
      } else {
        sessionStorage.removeItem('reviewQueue');
        sessionStorage.removeItem('reviewQueueTotal');
        toast.success(tr(
          `Successfully processed and saved ${ok} file(s)${storedTokens?.total > 0 ? ` · Tokens: ~${storedTokens.total.toLocaleString()}` : ''}.`,
          `已成功處理並儲存 ${ok} 個文件${storedTokens?.total > 0 ? ` · Tokens: ~${storedTokens.total.toLocaleString()}` : ''}。`,
          `已成功处理并储存 ${ok} 个文件${storedTokens?.total > 0 ? ` · Tokens: ~${storedTokens.total.toLocaleString()}` : ''}。`,
        ));
      }

      // 2026-11-05: the batch STAYS on this page — the stage machine takes
      // over (scanning while OCR jobs run, then Upload Complete with next-step
      // buttons). Imported ids stay stashed so the review pages' batch handoff
      // still ends on the File Storage highlight once everything is confirmed.
      const plan = postUploadPlan(importedIdsRef.current, reviewCount);
      if (plan.stash.length > 0) stashBatchHighlightIds(plan.stash);
      setCompleteDests(completeDestinations(batchRef.current, channel));
      setCompletePendingCount(reviewCount);
      setStage(queuedCount > 0 ? 'scanning' : 'complete');
    }
  };

  const handleSaveWithoutAI = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setFileErrors({});
    setFileStatuses({});
    setBatchProgress({ done: 0, total: files.length, currentFile: '' });

    let ok = 0;
    let hasError = false;
    let idx = 0;
    for (const file of files) {
      idx++;
      const fileIdx = idx - 1;
      setFileStatuses(prev => ({ ...prev, [fileIdx]: 'processing' }));
      try {
        setBatchProgress(prev => ({ ...prev, currentFile: file.name }));

        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsDataURL(file);
        });

        const activeClient = localStorage.getItem('activeClient');
        const headers: Record<string, string> = {};
        try { const c = JSON.parse(activeClient || '{}'); if (c?.id) headers['X-Active-Client'] = c.id; } catch {}

        await api('/file-storage/upload', {
          method: 'POST', baseUrl: WORKER_API_BASE,
          body: {
            filename: file.name, original_name: file.name,
            file_type: file.type, file_size: file.size,
            file_data: base64,
            folder: (CHANNELS.find(ch => ch.key === channel)?.folder) || 'Others',
            description, skip_ocr: true,
          },
        });

        ok++;
        setFileStatuses(prev => ({ ...prev, [fileIdx]: 'imported' }));
      } catch (e: any) {
        hasError = true;
        setFileStatuses(prev => ({ ...prev, [fileIdx]: 'error' }));
        setFileErrors(prev => ({ ...prev, [fileIdx]: e.message || 'Unknown error' }));
        break;
      }
    }

    setUploading(false);
    if (hasError) return;

    setFiles([]);
    setDescription('');
    setRejected([]);

    if (ok > 0) {
      queryClient.invalidateQueries({ queryKey: ['file-storage'] });
      toast.success(tr(
        `Saved ${ok} file(s) without AI analysis. Run Analyze later from File Storage.`,
        `已儲存 ${ok} 個文件（未經 AI 分析）。可稍後在檔案儲存庫中執行分析。`,
        `已储存 ${ok} 个文件（未经 AI 分析）。可稍后在文件存储库中执行分析。`,
      ));
      setTimeout(() => nav('/file-storage'), 800);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {windowDragActive && (
        <div className="pointer-events-none fixed inset-0 z-[100] flex items-center justify-center bg-primary/10 backdrop-blur-[2px]">
          <div className="rounded-xl border-4 border-dashed border-primary bg-card px-8 py-6 text-center shadow-xl">
            <Upload className="mx-auto h-10 w-10 text-primary" />
            <p className="mt-3 text-lg font-semibold">{tr('Drop files anywhere to upload', '隨處放開即可上傳', '随处放开即可上传')}</p>
            <p className="mt-1 text-sm text-muted-foreground">{tr('PDF, PNG, JPG, WEBP, GIF · max 10 MB each', '支援 PDF、PNG、JPG、WEBP、GIF · 每個檔案上限 10 MB', '支持 PDF、PNG、JPG、WEBP、GIF · 每个文件上限 10 MB')}</p>
          </div>
        </div>
      )}
      <h2 className="text-2xl font-bold flex items-center gap-2">
        <Upload className="h-6 w-6" /> {tr('Upload Document', '上傳文件', '上传文件')}
      </h2>

      {/* Stage views: scanning (OCR jobs running — also when navigating in
          mid-flight) / complete (next-step buttons) replace the selection UI;
          idle + uploading keep it. */}
      {view === 'scanning' ? (
        <ScanningView jobs={activeOcrJobs} />
      ) : view === 'complete' ? (
        <CompleteView dests={completeDests} pendingCount={completePendingCount} onUploadMore={resetToIdle} />
      ) : (
      <>
      {/* ── Document type ── */}
      <div className="bg-card border rounded-xl p-4">
        <div className="grid gap-3 sm:grid-cols-[220px_1fr] sm:items-end">
          <div>
            <label htmlFor="upload-document-type" className="block text-sm font-medium text-foreground mb-1">
              {tr('Document type', '文件類型', '文件类型')}
            </label>
            <select
              id="upload-document-type"
              value={channel}
              disabled={uploadsLocked}
              onChange={(event) => {
                const nextChannel = event.target.value as UploadChannel;
                setChannel(nextChannel);
                setFiles([]);
                setFileErrors({});
                setFileStatuses({});
                setRejected([]);
              }}
              className="w-full px-3 py-2 border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
            >
              <optgroup label={tr('Bank statements', '銀行月結單', '银行月结单')}>
                {CHANNELS.filter(ch => ch.category === 'bank_statement' || ch.key === 'bank_document_collection').map(ch => (
                  <option key={ch.key} value={ch.key}>{channelLabel(ch)}</option>
                ))}
              </optgroup>
              <optgroup label={tr('Other documents', '其他文件', '其他文件')}>
                {CHANNELS.filter(ch => ch.category !== 'bank_statement' && ch.key !== 'bank_document_collection').map(ch => (
                  <option key={ch.key} value={ch.key}>{channelLabel(ch)}</option>
                ))}
              </optgroup>
            </select>
          </div>

          <div className="text-sm text-muted-foreground sm:border-l sm:border-border sm:pl-4">
            <div>
              {tr('Upload destination', '上傳目的地', '上传目的地')}:{' '}
              <span className="font-medium text-foreground">{channelDef.folder}</span>
            </div>
            {channel === 'petty_cash' && (
              <p className="mt-1 rounded-lg border border-amber-300 bg-amber-50 px-2 py-1 text-sm font-medium text-amber-950 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-50">
                {tr('Files will create expenses under Petty Cash.', '文件將在零用金下建立支出。', '文件将在零用金下建立支出。')}
              </p>
            )}
            {channel === 'bank_document_collection' && (
              <p className="mt-1 rounded-lg border border-primary/25 bg-primary/5 px-2 py-1 text-sm font-medium text-foreground">
                {tr(
                  'Includes bank statements and their supporting bank documents. All files upload together as one collection.',
                  '包含銀行月結單及其支援銀行文件。所有文件會一起作為一個批次上傳。',
                  '包含银行月结单及其支持银行文件。所有文件会一起作为一个批次上传。',
                )}
              </p>
            )}
          </div>
        </div>
      </div>

      <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
        className={`bg-card border-2 border-dashed rounded-xl p-8 transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-border'}`}>
        {/* Kept outside the clickable prompt below: input.click() dispatches a
            bubbling click event, which would otherwise re-enter openPicker. */}
        <input ref={fileInputRef} type="file" accept={ACCEPT_ATTR} onChange={handleFileInput}
          className="sr-only" tabIndex={-1} multiple />

        <div onClick={openPicker}
          className={`flex flex-col items-center gap-4 ${uploading ? '' : 'cursor-pointer'}`}>
          <div className={`rounded-full p-4 transition-colors ${dragOver ? 'bg-primary/10' : 'bg-muted'}`}>
            <Upload className={`h-8 w-8 ${dragOver ? 'text-primary' : 'text-muted-foreground'}`} />
          </div>
          <div className="text-center">
            <p className="font-medium">{dragOver ? tr('Drop files here', '放開以上傳', '放開以上传') : tr('Drag & drop files here, or click to browse', '拖放文件至此，或點擊瀏覽', '拖放文件至此，或点击浏览')}</p>
            <p className="text-sm text-muted-foreground mt-1">{tr('Supports PDF, PNG, JPG, WEBP, GIF · max 10 MB each. OCR auto-detects bank statements, card statements & invoices.', '支援 PDF、PNG、JPG、WEBP、GIF · 每個檔案上限 10 MB。OCR 自動檢測銀行月結單、信用卡月結單及發票。', '支援 PDF、PNG、JPG、WEBP、GIF · 每个文件上限 10 MB。OCR 自动检测银行月结单、信用卡月结单及发票。')}</p>
          </div>
          <button type="button" disabled={uploadsLocked}
            onClick={e => { e.stopPropagation(); openPicker(); }}
            className="cursor-pointer bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-default">
            {uploading ? tr('Uploading...', '上傳中...', '上传中...') : tr('Select Files', '選擇文件', '选择文件')}
          </button>
        </div>

        {rejected.length > 0 && (
          <div className="mt-4 pt-4 border-t space-y-2">
            {rejected.map((r, i) => (
              <div key={i} className="flex items-start gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md">
                <AlertTriangle className="h-4 w-4 text-amber-900 dark:text-amber-100 shrink-0 mt-0.5" />
                <div className="min-w-0 text-xs">
                  <p className="font-medium text-amber-800 dark:text-amber-200 truncate">{r.file.name}</p>
                  <p className="text-amber-700 dark:text-amber-300 mt-0.5">{rejectionMessage(r.reason)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        {files.length > 0 && (
          <>
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium mb-2">{files.length} {tr('file(s) selected', '個文件已選擇', '个文件已选择')}</p>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                  {files.map((f, i) => {
                    const status = fileStatuses[i];
                    const errorMsg = fileErrors[i];
                    const isError = status === 'error';
                    const isImported = status === 'imported';
                    const isProcessing = status === 'processing';
                    const isQueued = status === 'queued';
                    const isReview = status === 'review';
                    const statusLabel = isImported
                      ? tr('Imported', '已導入', '已导入')
                      : isError
                        ? tr('Failed', '失敗', '失败')
                      : isQueued
                        ? tr('Queued — OCR scanning', '已排隊——OCR 掃描中', '已排队——OCR 扫描中')
                        : isReview
                          ? tr('Needs review', '需要審核', '需要审核')
                        : isProcessing
                          ? tr('Scanning', '掃描中', '扫描中')
                          : '';

                    return (
                      <div key={i} className="flex flex-col gap-1">
                        <div className={`flex items-center gap-2 text-sm ${isError ? 'text-red-900 dark:text-red-200 font-medium' : isImported ? 'text-green-900 dark:text-green-200' : isReview ? 'text-amber-900 dark:text-amber-200 font-medium' : 'text-muted-foreground'}`}>
                          {isError ? (
                            <AlertCircle className="h-4 w-4 text-red-900 dark:text-red-200 shrink-0" />
                          ) : isImported ? (
                            <CheckCircle2 className="h-4 w-4 text-green-900 dark:text-green-200 shrink-0" />
                          ) : isReview ? (
                            <AlertTriangle className="h-4 w-4 text-amber-900 dark:text-amber-200 shrink-0" />
                          ) : isProcessing ? (
                            <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
                        ) : f.type.includes('pdf') ? (
                          <FileText className="h-4 w-4 text-red-900 dark:text-red-200 shrink-0" />
                        ) : f.type.includes('image') ? (
                          <Image className="h-4 w-4 text-blue-900 dark:text-blue-200 shrink-0" />
                        ) : (
                          <File className="h-4 w-4 text-gray-900 dark:text-gray-200 shrink-0" />
                        )}
                          <span className="truncate">{f.name}</span>
                          <span className="text-xs shrink-0">({(f.size / 1024).toFixed(1)} KB)</span>
                          {statusLabel && (
                            <span className="text-xs font-medium shrink-0">{statusLabel}</span>
                          )}
                          {isError && (
                          <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-900 dark:text-red-200 text-xs font-bold shrink-0">!</span>
                        )}
                      </div>
                      {isError && errorMsg && (
                        <div className="ml-6 px-3 py-2 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-md text-xs text-red-700 dark:text-red-300">
                          {errorMsg}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs text-muted-foreground">{tr('Description', '描述', '描述')}</label>
                <input value={description} onChange={e => setDescription(e.target.value)} placeholder={tr('Optional description', '可選描述', '可选描述')}
                  className="px-3 py-2 border rounded-md bg-background text-sm w-full focus:outline-none focus:ring-2 focus:ring-ring" />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => { setFiles([]); setDescription(''); setFileErrors({}); setFileStatuses({}); setRejected([]); }}
                className="px-4 py-2 border rounded-md text-sm hover:bg-muted">{tr('Clear', '清除', '清除')}</button>
              <button onClick={handleUpload} disabled={uploadsLocked}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
                {uploading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {channel === 'bank_document_collection'
                    ? tr('Upload Bank Document Collection', '上傳銀行文件批次', '上传银行文件批次')
                  : tr('Upload & Analyze', '上傳並分析', '上传并分析')}
              </button>
              {channel !== 'bank_document_collection' && (
                <button onClick={handleSaveWithoutAI} disabled={uploadsLocked || files.length === 0}
                  className="px-4 py-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2">
                  {tr('Save without AI Analysis', '儲存（不用 AI 分析）', '储存（不用 AI 分析）')}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {processingMsg && (
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-primary shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium">{processingMsg}</p>
              {batchProgress.total > 1 && batchProgress.currentFile && (
                <p className="text-xs text-muted-foreground truncate">{batchProgress.currentFile}</p>
              )}
              <p className="text-xs text-muted-foreground">{tr('AI is extracting transactions…', 'AI 正在提取交易記錄…', 'AI 正在提取交易记录…')}</p>
            </div>
          </div>
          {batchProgress.total > 1 && (
            <div className="space-y-1">
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-500"
                  style={{ width: `${batchProgress.total > 0 ? (batchProgress.done / batchProgress.total) * 100 : 0}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground text-right">
                {batchProgress.done} / {batchProgress.total}
              </p>
            </div>
          )}
        </div>
      )}
      </>
      )}

      {/* Idle-state stats: uploaded + pending counts per type, linking to
          Pending Review (follows the FY/date selector). */}
      {view === 'idle' && <UploadStatsPanel />}

      {/* ── OCR mismatch dialog ── */}
      {mismatch && (
        <MismatchDialog
          info={mismatch}
          onForce={() => { const resolve = mismatchResolveRef.current; setMismatch(null); resolve?.('force'); }}
          onSwitch={() => { const resolve = mismatchResolveRef.current; setMismatch(null); resolve?.('switch'); }}
          onClose={() => { const resolve = mismatchResolveRef.current; setMismatch(null); resolve?.('cancel'); }}
        />
      )}

      {/* ── Encrypted PDF password prompt ── */}
      {encryptedFile && (
        <EncryptedPdfModal
          fileId={encryptedFile.fileId}
          fileName={encryptedFile.fileName}
          onClose={() => setEncryptedFile(null)}
          onSuccess={() => {
            setEncryptedFile(null);
            toast.success(tr(
              'PDF decrypted! Your bank statement is now being processed.',
              'PDF 已解密！銀行月結單正在處理中。',
              'PDF 已解密！银行月结单正在处理中。'
            ));
          }}
        />
      )}

      {/* ── Auto-generate suggestion panel ── */}
      {showAutoGenPanel && (
        <AutoGenerateSuggestionPanel onDone={() => setShowAutoGenPanel(false)} />
      )}
    </div>
  );
}
